/**
 * Collections-tree multi-selection: the pure half.
 *
 * These functions know nothing about React. They answer four questions the
 * sidebar's marked set needs answered, and every one of them is a pure function
 * of `collections` + the expansion sets, so they are unit-testable without
 * rendering a tree.
 *
 * Row ids are the SAME prefixed ids the drag-and-drop layer already puts in the
 * DOM as `data-drag-id` — `collection-<id>` | `folder-<id>` | `request-<id>` —
 * so nothing new is invented and a marked id is directly comparable with a drag
 * id. `parseRowId` is the single parser for that scheme; `Sidebar.js` imports it
 * rather than keeping its own copy.
 */

import {
  getOrderedChildren,
  ancestorFolderIds,
  normalizeParentId,
} from './collectionFolders';

// ─── Row ids ─────────────────────────────────────────────────────────────────

export function rowIdFor(kind, id) {
  return `${kind}-${id}`;
}

export function parseRowId(id) {
  const s = String(id);
  if (s.startsWith('collection-')) return { kind: 'collection', key: s.slice('collection-'.length) };
  if (s.startsWith('folder-'))     return { kind: 'folder',     key: s.slice('folder-'.length) };
  if (s.startsWith('request-'))    return { kind: 'request',    key: s.slice('request-'.length) };
  return null;
}

// ─── Visible row order ───────────────────────────────────────────────────────

/**
 * The rows the user can actually SEE, top to bottom.
 *
 * ⚠️  READ THIS BEFORE "FIXING" IT.  A COLLAPSED FOLDER CONTRIBUTES EXACTLY ONE
 * ROW — ITSELF.  Its hidden children are deliberately NOT part of the order, and
 * therefore never land in a shift-click range.
 *
 * The temptation is to walk the full logical tree so that shift-clicking "past"
 * a collapsed folder also takes what is inside it. Do not. That definition means
 * a user who drags a shift-click across a collapsed folder silently marks the
 * forty requests hidden inside it and then confirms a delete that names a count
 * they never saw. The range is over what is on screen, because that is the only
 * definition the user can verify with their eyes.
 *
 * The expansion predicates are exactly the ones `Sidebar` renders with
 * (`expandedCollections.has(collection.id)` / `expandedFolders.has(folder.id)`),
 * so "visible" here is the same "visible" the DOM has, by construction.
 *
 * @param {Array}  collections            the collections AS RENDERED (i.e. already search-filtered)
 * @param {Object} opts
 * @param {Set}    opts.expandedCollections
 * @param {Set}    opts.expandedFolders
 * @returns {string[]} ordered row ids
 */
export function flattenVisibleRows(collections, { expandedCollections, expandedFolders } = {}) {
  const cols = Array.isArray(collections) ? collections : [];
  const openCollections = expandedCollections || new Set();
  const openFolders = expandedFolders || new Set();
  const rows = [];

  const walk = (collection, parentFolderId) => {
    for (const entry of getOrderedChildren(collection, parentFolderId)) {
      if (entry.type === 'folder') {
        rows.push(rowIdFor('folder', entry.id));
        if (openFolders.has(entry.id)) walk(collection, entry.id);
      } else {
        rows.push(rowIdFor('request', entry.id));
      }
    }
  };

  for (const collection of cols) {
    if (!collection || collection.id == null) continue;
    rows.push(rowIdFor('collection', collection.id));
    if (openCollections.has(collection.id)) walk(collection, null);
  }
  return rows;
}

/**
 * The inclusive span of visible rows between the anchor and the target.
 * Falls back to just the target when the anchor is gone (collapsed away,
 * deleted, filtered out by search) — never to "everything".
 */
export function rangeBetween(rows, anchorId, targetId) {
  const list = Array.isArray(rows) ? rows : [];
  const to = list.indexOf(targetId);
  if (to === -1) return anchorId && anchorId !== targetId ? [] : [targetId];
  const from = anchorId == null ? -1 : list.indexOf(anchorId);
  if (from === -1) return [targetId];
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  return list.slice(lo, hi + 1);
}

// ─── Index over the collections ──────────────────────────────────────────────

function buildIndex(collections) {
  const cols = Array.isArray(collections) ? collections : [];
  const collectionById = new Map();
  const folderOwner = new Map();   // folderId  -> { collection, folder }
  const requestOwner = new Map();  // requestId -> { collection, request }

  for (const c of cols) {
    if (!c || c.id == null) continue;
    collectionById.set(String(c.id), c);
    for (const f of c.settings?.folders || []) {
      folderOwner.set(String(f.id), { collection: c, folder: f });
    }
    for (const r of c.requests || []) {
      requestOwner.set(String(r.id), { collection: c, request: r });
    }
  }
  return { collectionById, folderOwner, requestOwner };
}

/**
 * Resolve a marked row id to the record shapes the EXISTING single-item
 * handlers already take, so the bulk path can hand them straight over:
 *   collection → the collection
 *   folder     → the folder + `collectionId` (what `onDeleteFolder` expects)
 *   request    → the request
 * Unknown / stale ids resolve to null and are dropped by the callers.
 */
export function resolveRowId(index, id) {
  const parsed = parseRowId(id);
  if (!parsed) return null;
  if (parsed.kind === 'collection') {
    const collection = index.collectionById.get(String(parsed.key));
    return collection ? { id, kind: 'collection', collection, item: collection } : null;
  }
  if (parsed.kind === 'folder') {
    const hit = index.folderOwner.get(String(parsed.key));
    if (!hit) return null;
    return {
      id,
      kind: 'folder',
      collection: hit.collection,
      item: { ...hit.folder, collectionId: hit.collection.id },
    };
  }
  const hit = index.requestOwner.get(String(parsed.key));
  if (!hit) return null;
  return { id, kind: 'request', collection: hit.collection, item: hit.request };
}

/** Row ids of every strict ancestor of `id` (collection first, then the folder chain). */
function ancestorRowIds(index, id) {
  const parsed = parseRowId(id);
  if (!parsed) return [];
  if (parsed.kind === 'collection') return [];

  if (parsed.kind === 'folder') {
    const hit = index.folderOwner.get(String(parsed.key));
    if (!hit) return [];
    // `ancestorFolderIds` starts AT the folder itself, so drop the head.
    const chain = ancestorFolderIds(hit.collection, parsed.key).slice(1);
    return [rowIdFor('collection', hit.collection.id), ...chain.map((f) => rowIdFor('folder', f))];
  }

  const hit = index.requestOwner.get(String(parsed.key));
  if (!hit) return [];
  const folderId = normalizeParentId(hit.request.folder_id);
  const chain = folderId ? ancestorFolderIds(hit.collection, folderId) : [];
  return [rowIdFor('collection', hit.collection.id), ...chain.map((f) => rowIdFor('folder', f))];
}

/**
 * Drop every marked node that already has a marked ANCESTOR, and every id that
 * no longer resolves. Marking stays literal — the user sees exactly what they
 * clicked — and normalisation happens at ACTION time, once, here.
 *
 * Without it:
 *  · delete of {folder-7, request-42∈7} issues a DELETE for a request the folder
 *    delete already removed → a 404, a caught error and a spurious failure count;
 *  · move of the same pair moves 42 twice, the second time from a location it no
 *    longer occupies.
 *
 * Input order is preserved.
 */
export function normalizeSelection(ids, collections) {
  const list = [...(ids || [])];
  const index = buildIndex(collections);
  const marked = new Set(list.map(String));
  return list.filter((id) => {
    if (!resolveRowId(index, id)) return false;
    return !ancestorRowIds(index, id).some((a) => marked.has(a));
  });
}

/**
 * Everything the selection context menu needs to render itself, derived once.
 *
 * `canDelete` / `canMove` are the gates the menu binds to — a read-only member
 * of the selection disables the whole action rather than being silently skipped,
 * matching how every existing per-item entry gates on `can_edit`.
 */
export function describeSelection(ids, collections) {
  const index = buildIndex(collections);
  const normalized = normalizeSelection(ids, collections);
  const resolved = normalized.map((id) => resolveRowId(index, id)).filter(Boolean);

  const counts = { collections: 0, folders: 0, requests: 0 };
  let readOnly = false;
  for (const entry of resolved) {
    if (entry.kind === 'collection') counts.collections += 1;
    else if (entry.kind === 'folder') counts.folders += 1;
    else counts.requests += 1;
    if (entry.collection?.can_edit === false) readOnly = true;
    if (entry.kind === 'request' && entry.item?.can_edit === false) readOnly = true;
  }

  const total = resolved.length;
  const hasCollection = counts.collections > 0;
  return {
    items: resolved,
    ids: normalized,
    total,
    counts,
    names: resolved.map((e) => e.item?.name).filter(Boolean),
    readOnly,
    hasCollection,
    canDelete: total > 0 && !readOnly,
    // Same gate as Delete: a copy is a WRITE into the parent collection, and a
    // read-only member disables the action rather than being skipped silently.
    canDuplicate: total > 0 && !readOnly,
    // A collection has no parent to be moved INTO, so any collection in the mix
    // disables Move rather than being quietly dropped from it.
    canMove: total > 0 && !readOnly && !hasCollection,
  };
}
